/* eslint-disable no-console */

import {
	type OperationalEventAction,
	type OperationalEventAttributes,
	sendUnsampledPackageOperationalEvent,
} from '../analytics-client';
import { LOG_PREFIX } from './constants';
import { determineLogLevel } from './determine-log-level';
import { shouldSendErrorEvent } from './error-event-throttle';
import { LogLevel } from './log-level';

type LoggingMetadata = string | { [k: string]: string };

interface OperationalErrorLogConfig {
	action: OperationalEventAction;
	attributes?: OperationalEventAttributes;
	message: string;
	metadata?: LoggingMetadata;
}

const formatMetadata = (metadata?: LoggingMetadata): string => {
	if (!metadata) {
		return '';
	}
	// `LoggingMetadata` allows a bare string; emit it as-is rather than running it
	// through JSON.stringify, which would wrap it in extra quotes ("error-code" -> '"error-code"').
	return typeof metadata === 'string' ? metadata : JSON.stringify(metadata);
};

export class Logger {
	// Console output is controlled solely by the resolved log level. `DEFAULT_LOG_LEVEL` is ERROR,
	// so info/warn/debug stay silent unless a consumer raises `packageLogLevel` or a developer sets
	// the `atl-bsc-debug-mode` cookie. ERROR output is intentionally always on.
	//
	// Keep these guards free of `fg` calls: `Logger.*` runs at module-load time in some consumers
	// (e.g. the AtlIndexedDb singleton), and evaluating a feature gate during a plain import breaks
	// consumers whose tests mock `fg` with a not-yet-initialized factory variable. The one gate this
	// path can still reach is handled in determine-log-level.ts, which checks its cookie first.
	static info(message: string, metadata?: LoggingMetadata): void {
		if (determineLogLevel() >= LogLevel.INFO) {
			console.info(`${LOG_PREFIX}: ${message}`, formatMetadata(metadata));
		}
	}

	static warn(message: string, metadata?: LoggingMetadata): void {
		if (determineLogLevel() >= LogLevel.WARN) {
			console.warn(`${LOG_PREFIX}: ${message}`, formatMetadata(metadata));
		}
	}

	static error(message: string, metadata?: LoggingMetadata): void {
		if (determineLogLevel() >= LogLevel.ERROR) {
			console.error(`${LOG_PREFIX}: ${message}`, formatMetadata(metadata));
		}
	}

	static debug(message: string, metadata?: LoggingMetadata): void {
		if (determineLogLevel() >= LogLevel.DEBUG) {
			console.debug(`${LOG_PREFIX}: DEBUG ${message}`, formatMetadata(metadata));
		}
	}

	/**
	 * Sends an unsampled operational event to backend telemetry and also emits the message via
	 * `Logger.error`. The telemetry send is throttled per-action (see error-event-throttle). The
	 * console output is always at ERROR level (subject to the resolved log level).
	 */
	static errorWithOperationalEvent({
		action,
		attributes = {},
		message,
		metadata,
	}: OperationalErrorLogConfig): void {
		try {
			// shouldSendErrorEvent records the send and limits per-action volume, so a repeatedly
			// failing call site cannot flood telemetry with duplicate events.
			if (shouldSendErrorEvent(action)) {
				sendUnsampledPackageOperationalEvent({
					action,
					attributes: { error: message, ...attributes },
				});
			}
		} catch (e: any) {
			this.error(`Failed to send operational event on error: ${action}. ${e.message || ''}`);
		}

		this.error(message, metadata);
	}
}
